import React, { useState, useEffect, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { FaAward, FaBookReader, FaUsers, FaHandsHelping, FaHeartbeat, FaGraduationCap, FaChild, FaMedal, FaCertificate, FaTrophy, FaChevronDown, FaPhone, FaEnvelope, FaMapMarkerAlt, FaShieldAlt, FaChartLine, FaCalendarAlt, FaEye } from 'react-icons/fa';

// Animation variants
const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};



const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.15,
            delayChildren: 0.1
        }
    }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: "easeOut" }
    }
};

const slideInFromBottom = {
    hidden: { opacity: 0, y: 50 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

// Custom hook for intersection observer
const useIntersectionObserver = (ref, options = {}) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const currentRef = ref.current;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
            }
        }, {
            threshold: 0.1,
            ...options
        });

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [ref, options]);

    return isVisible;
};

// Custom hook for animated counter
const useAnimatedCounter = (targetValue, duration = 2000, shouldStart = false) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!shouldStart) return;

        let startTime;
        let animationFrame;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);

            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentCount = Math.floor(easeOutQuart * targetValue);

            setCount(currentCount);

            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            } else {
                setCount(targetValue);
            }
        };

        animationFrame = requestAnimationFrame(animate);

        return () => {
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [targetValue, duration, shouldStart]);

    return count;
};

// Animated Counter Component
const AnimatedCounter = ({ targetValue, suffix = '', label, shouldStart, icon, color = "blue" }) => {
    const count = useAnimatedCounter(targetValue, 2500, shouldStart);

    const colorClasses = {
        blue: "text-blue-600 bg-blue-50",
        green: "text-green-600 bg-green-50",
        purple: "text-purple-600 bg-purple-50",
        red: "text-red-600 bg-red-50",
        indigo: "text-indigo-600 bg-indigo-50",
        orange: "text-orange-600 bg-orange-50"
    };

    return (
        <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 text-center relative overflow-hidden"
            variants={scaleIn}
            whileHover={{
                y: -10,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                scale: 1.02
            }}
            transition={{ duration: 0.3 }}
        >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-600"></div>
            <div className={`w-16 h-16 ${colorClasses[color]} rounded-full flex items-center justify-center mx-auto mb-4 text-2xl`}>
                {icon}
            </div>
            <div className="text-4xl font-bold text-gray-800 mb-2">
                {count.toLocaleString()}{suffix}
            </div>
            <div className="text-gray-600 font-medium uppercase text-sm tracking-wide">{label}</div>
        </motion.div>
    );
};

const achievementsData = [
    {
        icon: <FaGraduationCap />,
        image: "/images/IMG-20250605-WA0404.jpg",
        title: "Education Program Success",
        date: "2016-2024",
        description: "Successfully provided quality education to over 2000 underprivileged children in Varanasi slums. Our educational programs have significantly improved literacy rates and decision-making capabilities among participants.",
        metric: "2000+ Children Educated",
        category: "Education",
        impact: "High",
        color: "blue"
    },
    {
        icon: <FaHeartbeat />,
        image: "/images/IMG-20250605-WA0405.jpg",
        title: "Community Health Camps",
        date: "2017-2024",
        description: "Organized free health camps and awareness programs in rural villages around Varanasi. These initiatives have provided healthcare access to families who previously had limited medical support.",
        metric: "500+ Families Reached",
        category: "Health",
        impact: "High",
        color: "red"
    },
    {
        icon: <FaUsers />,
        image: "/images/IMG-20250605-WA0417.jpg",
        title: "Vocational Training Success",
        date: "2018-2024",
        description: "Launched successful vocational training programs for children and unemployed women. These programs focus on building practical skills that lead to sustainable employment and income generation.",
        metric: "300+ People Trained",
        category: "Training",
        impact: "Medium",
        color: "green"
    },
    {
        icon: <FaChild />,
        image: "/images/IMG-20250605-WA0418.jpg",
        title: "Children Development Programs",
        date: "2017-2024",
        description: "Organized children into groups and provided personality development lessons. Created positive environments for slum children through recreational activities and social exposure programs.",
        metric: "1000+ Children Participated",
        category: "Development",
        impact: "High",
        color: "purple"
    },
    {
        icon: <FaShieldAlt />,
        image: "/images/IMG-20250605-WA0419.jpg",
        title: "Self-Defence Training Initiative",
        date: "2019-2024",
        description: "Successfully conducted self-defence training programs for children and youth. These programs have empowered participants with confidence and safety skills.",
        metric: "200+ Youth Trained",
        category: "Empowerment",
        impact: "Medium",
        color: "orange"
    },
    {
        icon: <FaMedal />,
        image: "/images/IMG-20250605-WA0375.jpg",
        title: "Educational Excellence Awards",
        date: "2020-2024",
        description: "Recognized several children from our programs who achieved excellent academic results. These achievements demonstrate the effectiveness of our educational interventions.",
        metric: "50+ Awards Distributed",
        category: "Recognition",
        impact: "Medium",
        color: "indigo"
    },
    {
        icon: <FaCertificate />,
        image: "/images/IMG-20250605-WA0372.jpg",
        title: "Women Empowerment Certificates",
        date: "2018-2024",
        description: "Successfully organized women into self-help groups and provided leadership training. Many women have now taken on decision-making roles in their communities.",
        metric: "150+ Women Certified",
        category: "Empowerment",
        impact: "High",
        color: "purple"
    },
    {
        icon: <FaTrophy />,
        image: "/images/IMG-20250605-WA0377.jpg",
        title: "Community Recognition",
        date: "2019-2024",
        description: "Received appreciation from local community leaders for our consistent work in slum areas of Varanasi. Our holistic approach to child development has been widely recognized.",
        metric: "Community Appreciation",
        category: "Recognition",
        impact: "High",
        color: "indigo"
    },
    {
        icon: <FaBookReader />,
        image: "/images/IMG-20250605-WA0380.jpg",
        title: "Literacy Campaign Success",
        date: "2018-2023",
        description: "Conducted successful literacy campaigns creating awareness about the importance of education. These campaigns have resulted in increased school enrollment in target areas.",
        metric: "25+ Villages Covered",
        category: "Education",
        impact: "High",
        color: "blue"
    },
    {
        icon: <FaAward />,
        image: "/images/IMG-20250605-WA0381.jpg",
        title: "Child Protection Initiatives",
        date: "2017-2024",
        description: "Successfully implemented programs to eliminate child labor and promote gender equality. Our efforts have contributed to protecting vulnerable children from exploitation.",
        metric: "100+ Children Protected",
        category: "Protection",
        impact: "High",
        color: "red"
    }
];

const impactStats = [
    {
        number: 2000,
        suffix: "+",
        label: "Children Educated",
        icon: <FaGraduationCap />,
        color: "blue"
    },
    {
        number: 500,
        suffix: "+",
        label: "Families Supported",
        icon: <FaUsers />,
        color: "green"
    },
    {
        number: 25,
        suffix: "+",
        label: "Villages Reached",
        icon: <FaHandsHelping />,
        color: "purple"
    },
    {
        number: 8,
        suffix: "+",
        label: "Years of Service",
        icon: <FaAward />,
        color: "red"
    },
    {
        number: 300,
        suffix: "+",
        label: "Skills Trained",
        icon: <FaChartLine />,
        color: "indigo"
    },
    {
        number: 100,
        suffix: "%",
        label: "Community Trust",
        icon: <FaHeartbeat />,
        color: "orange"
    }
];

const AchievementsPage = () => {
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedImpact, setSelectedImpact] = useState('All');
    const statsRef = useRef(null);
    const isStatsVisible = useIntersectionObserver(statsRef);

    const categories = ['All', 'Education', 'Health', 'Training', 'Development', 'Empowerment', 'Recognition', 'Protection'];
    const impactLevels = ['All', 'High', 'Medium'];

    const filteredAchievements = achievementsData.filter(achievement => {
        const categoryMatch = selectedCategory === 'All' || achievement.category === selectedCategory;
        const impactMatch = selectedImpact === 'All' || achievement.impact === selectedImpact;
        return categoryMatch && impactMatch;
    });

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <motion.section
                className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden"
                initial="hidden"
                animate="visible"
            >
                <div className="absolute inset-0">
                    <img
                        src="/images/IMG-20250605-WA0378.jpg"
                        alt="Ummeed Ki Kiran Foundation Achievements"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-purple-800/80 to-indigo-900/90"></div>
                </div>

                <div className="relative z-10 max-w-6xl mx-auto px-4 text-center text-white">
                    <motion.div variants={staggerContainer}>
                        <motion.h1
                            className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent"
                            variants={fadeInUp}
                        >
                            Our Achievements
                        </motion.h1>
                        <motion.p
                            className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed"
                            variants={fadeInUp}
                        >
                            8 years of transforming lives in Varanasi. Each milestone represents hope, education, and empowerment for underprivileged children.
                        </motion.p>
                        <motion.div
                            className="flex flex-wrap justify-center gap-6"
                            variants={fadeInUp}
                        >
                            <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 flex items-center gap-3">
                                <FaTrophy className="text-yellow-400 text-xl" />
                                <span className="font-semibold">Since 2016</span>
                            </div>
                            <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 flex items-center gap-3">
                                <FaUsers className="text-blue-400 text-xl" />
                                <span className="font-semibold">2000+ Lives Touched</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Impact Statistics Section */}
            <motion.section
                className="py-20 bg-gradient-to-br from-gray-50 to-blue-50"
                ref={statsRef}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-16" variants={staggerContainer}>
                        <motion.h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
                            Impact by Numbers
                        </motion.h2>
                        <motion.p className="text-xl text-gray-600 max-w-3xl mx-auto" variants={fadeInUp}>
                            These numbers represent real lives transformed, communities empowered, and dreams realized through our dedicated efforts.
                        </motion.p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                        variants={staggerContainer}
                    >
                        {impactStats.map((stat, index) => (
                            <AnimatedCounter
                                key={index}
                                targetValue={stat.number}
                                suffix={stat.suffix}
                                label={stat.label}
                                icon={stat.icon}
                                color={stat.color}
                                shouldStart={isStatsVisible}
                            />
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Focus Areas Section */}
            <motion.section
                className="py-20 bg-white"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-16" variants={staggerContainer}>
                        <motion.h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
                            Our Impact Areas
                        </motion.h2>
                        <motion.p className="text-xl text-gray-600" variants={fadeInUp}>
                            Comprehensive approach to community development
                        </motion.p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-3 gap-8"
                        variants={staggerContainer}
                    >
                        <motion.div
                            className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl text-center group"
                            variants={scaleIn}
                            whileHover={{ y: -10, scale: 1.02 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl group-hover:scale-110 transition-transform">
                                <FaGraduationCap />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">Quality Education</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Providing comprehensive educational programs to underprivileged children with focus on literacy and life skills
                            </p>
                        </motion.div>

                        <motion.div
                            className="bg-gradient-to-br from-red-50 to-red-100 p-8 rounded-2xl text-center group"
                            variants={scaleIn}
                            whileHover={{ y: -10, scale: 1.02 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl group-hover:scale-110 transition-transform">
                                <FaHeartbeat />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">Healthcare Access</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Ensuring health and wellness through free medical camps, awareness programs, and primary healthcare access
                            </p>
                        </motion.div>

                        <motion.div
                            className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl text-center group"
                            variants={scaleIn}
                            whileHover={{ y: -10, scale: 1.02 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl group-hover:scale-110 transition-transform">
                                <FaUsers />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">Community Development</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Building stronger communities through holistic development programs and sustainable initiatives
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Achievements Section */}
            <motion.section
                className="py-20 bg-gradient-to-br from-gray-50 to-purple-50"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-16" variants={staggerContainer}>
                        <motion.h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
                            Journey of Achievements
                        </motion.h2>
                        <motion.p className="text-xl text-gray-600 max-w-4xl mx-auto" variants={fadeInUp}>
                            From education to health, from vocational training to child protection - our comprehensive approach has created lasting impact across multiple domains.
                        </motion.p>
                    </motion.div>

                    {/* Enhanced Filter System */}
                    <motion.div className="mb-12" variants={staggerContainer}>
                        <motion.div className="mb-8" variants={fadeInUp}>
                            <h3 className="text-lg font-semibold text-gray-700 mb-4 text-center">Filter by Category</h3>
                            <div className="flex flex-wrap justify-center gap-3">
                                {categories.map((category) => (
                                    <motion.button
                                        key={category}
                                        className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${selectedCategory === category
                                            ? 'bg-blue-600 text-white shadow-lg'
                                            : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                                            }`}
                                        onClick={() => setSelectedCategory(category)}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        {category}
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div variants={fadeInUp}>
                            <h3 className="text-lg font-semibold text-gray-700 mb-4 text-center">Filter by Impact Level</h3>
                            <div className="flex flex-wrap justify-center gap-3">
                                {impactLevels.map((impact) => (
                                    <motion.button
                                        key={impact}
                                        className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${selectedImpact === impact
                                            ? 'bg-purple-600 text-white shadow-lg'
                                            : 'bg-white text-gray-600 hover:bg-purple-50 hover:text-purple-600'
                                            }`}
                                        onClick={() => setSelectedImpact(impact)}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        {impact} Impact
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* Enhanced Achievements Grid */}
                    <motion.div
                        className="grid grid-cols-1 lg:grid-cols-2 gap-8"
                        variants={staggerContainer}
                        key={`${selectedCategory}-${selectedImpact}`}
                    >
                        {filteredAchievements.map((achievement, index) => (
                            <motion.article
                                key={index}
                                className="bg-white rounded-2xl shadow-lg overflow-hidden group"
                                variants={slideInFromBottom}
                                whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="relative overflow-hidden h-64">
                                    <img
                                        src={achievement.image}
                                        alt={achievement.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className="absolute top-4 left-4">
                                        <span className={`px-3 py-1 text-white text-sm font-medium rounded-full ${achievement.color === 'blue' ? 'bg-blue-600' :
                                            achievement.color === 'red' ? 'bg-red-600' :
                                                achievement.color === 'green' ? 'bg-green-600' :
                                                    achievement.color === 'purple' ? 'bg-purple-600' :
                                                        achievement.color === 'orange' ? 'bg-orange-600' :
                                                            achievement.color === 'indigo' ? 'bg-indigo-600' : 'bg-blue-600'
                                            }`}>
                                            {achievement.category}
                                        </span>
                                    </div>
                                    <div className="absolute top-4 right-4">
                                        <span className={`px-3 py-1 ${achievement.impact === 'High' ? 'bg-green-500' : 'bg-yellow-500'
                                            } text-white text-sm font-medium rounded-full flex items-center gap-1`}>
                                            <FaEye className="text-xs" />
                                            {achievement.impact}
                                        </span>
                                    </div>
                                    <div className="absolute bottom-4 right-4">
                                        <div className={`w-12 h-12 text-white rounded-full flex items-center justify-center text-xl ${achievement.color === 'blue' ? 'bg-blue-600' :
                                            achievement.color === 'red' ? 'bg-red-600' :
                                                achievement.color === 'green' ? 'bg-green-600' :
                                                    achievement.color === 'purple' ? 'bg-purple-600' :
                                                        achievement.color === 'orange' ? 'bg-orange-600' :
                                                            achievement.color === 'indigo' ? 'bg-indigo-600' : 'bg-blue-600'
                                            }`}>
                                            {achievement.icon}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-8">
                                    <div className="flex items-center gap-3 mb-4">
                                        <FaCalendarAlt className="text-gray-400" />
                                        <span className="text-gray-500 font-medium">{achievement.date}</span>
                                    </div>

                                    <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-blue-600 transition-colors">
                                        {achievement.title}
                                    </h3>

                                    <p className="text-gray-600 leading-relaxed mb-6">
                                        {achievement.description}
                                    </p>

                                    <div className={`rounded-lg p-4 ${achievement.color === 'blue' ? 'bg-blue-50 border border-blue-200' :
                                        achievement.color === 'red' ? 'bg-red-50 border border-red-200' :
                                            achievement.color === 'green' ? 'bg-green-50 border border-green-200' :
                                                achievement.color === 'purple' ? 'bg-purple-50 border border-purple-200' :
                                                    achievement.color === 'orange' ? 'bg-orange-50 border border-orange-200' :
                                                        achievement.color === 'indigo' ? 'bg-indigo-50 border border-indigo-200' : 'bg-blue-50 border border-blue-200'
                                        }`}>
                                        <div className="flex items-center gap-2">
                                            <FaChartLine className={`${achievement.color === 'blue' ? 'text-blue-600' :
                                                achievement.color === 'red' ? 'text-red-600' :
                                                    achievement.color === 'green' ? 'text-green-600' :
                                                        achievement.color === 'purple' ? 'text-purple-600' :
                                                            achievement.color === 'orange' ? 'text-orange-600' :
                                                                achievement.color === 'indigo' ? 'text-indigo-600' : 'text-blue-600'
                                                }`} />
                                            <span className="font-semibold text-gray-700">Key Impact:</span>
                                        </div>
                                        <p className={`font-bold text-lg mt-1 ${achievement.color === 'blue' ? 'text-blue-700' :
                                            achievement.color === 'red' ? 'text-red-700' :
                                                achievement.color === 'green' ? 'text-green-700' :
                                                    achievement.color === 'purple' ? 'text-purple-700' :
                                                        achievement.color === 'orange' ? 'text-orange-700' :
                                                            achievement.color === 'indigo' ? 'text-indigo-700' : 'text-blue-700'
                                            }`}>
                                            {achievement.metric}
                                        </p>
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </motion.div>

                    {filteredAchievements.length === 0 && (
                        <motion.div
                            className="text-center py-20"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className="text-6xl text-gray-300 mb-4">🔍</div>
                            <h3 className="text-2xl font-semibold text-gray-600 mb-2">No achievements found</h3>
                            <p className="text-gray-500">Try adjusting your filters to see more results</p>
                        </motion.div>
                    )}
                </div>
            </motion.section>

            {/* Recognition Timeline Section */}
            <motion.section
                className="py-20 bg-white"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-16" variants={staggerContainer}>
                        <motion.h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
                            Recognition & Milestones
                        </motion.h2>
                        <motion.p className="text-xl text-gray-600" variants={fadeInUp}>
                            Key moments that shaped our journey of impact
                        </motion.p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
                        variants={staggerContainer}
                    >
                        <motion.div
                            className="text-center group"
                            variants={scaleIn}
                            whileHover={{ y: -10 }}
                        >
                            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl shadow-lg group-hover:scale-110 transition-transform">
                                <FaCertificate />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-3">NGO Registration</h3>
                            <p className="text-gray-600 mb-4">Registered under the Indian Trust Act</p>
                            <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold">2016</span>
                        </motion.div>

                        <motion.div
                            className="text-center group"
                            variants={scaleIn}
                            whileHover={{ y: -10 }}
                        >
                            <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl shadow-lg group-hover:scale-110 transition-transform">
                                <FaTrophy />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-3">Community Trust</h3>
                            <p className="text-gray-600 mb-4">Earned trust of Varanasi slum communities</p>
                            <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">2017-2024</span>
                        </motion.div>

                        <motion.div
                            className="text-center group"
                            variants={scaleIn}
                            whileHover={{ y: -10 }}
                        >
                            <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl shadow-lg group-hover:scale-110 transition-transform">
                                <FaMedal />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-3">Educational Impact</h3>
                            <p className="text-gray-600 mb-4">Significant improvement in literacy rates</p>
                            <span className="inline-block bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-semibold">2018-2024</span>
                        </motion.div>

                        <motion.div
                            className="text-center group"
                            variants={scaleIn}
                            whileHover={{ y: -10 }}
                        >
                            <div className="w-24 h-24 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl shadow-lg group-hover:scale-110 transition-transform">
                                <FaAward />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-3">Holistic Approach</h3>
                            <p className="text-gray-600 mb-4">Comprehensive child development programs</p>
                            <span className="inline-block bg-red-100 text-red-800 px-4 py-2 rounded-full font-semibold">2019-2024</span>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Call to Action Section */}
            <motion.section
                className="py-20 relative overflow-hidden"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="absolute inset-0">
                    <img
                        src="/images/IMG-20250605-WA0396.jpg"
                        alt="Join Our Mission"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 to-purple-900/95"></div>
                </div>

                <div className="relative z-10 max-w-4xl mx-auto px-4 text-center text-white">
                    <motion.div variants={staggerContainer}>
                        <motion.h2 className="text-4xl md:text-5xl font-bold mb-6" variants={fadeInUp}>
                            Be Part of Our Next Achievement
                        </motion.h2>
                        <motion.p className="text-xl mb-12 leading-relaxed" variants={fadeInUp}>
                            Your support can help us reach even more children and create greater impact in Varanasi slums.
                            Join us in writing the next chapter of our success story.
                        </motion.p>

                        <motion.div className="flex flex-col sm:flex-row gap-6 justify-center mb-12" variants={fadeInUp}>
                            <motion.a
                                href="/donate"
                                className="bg-white text-blue-600 hover:bg-blue-50 font-bold py-4 px-8 rounded-full text-lg shadow-lg"
                                whileHover={{ scale: 1.05, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                                whileTap={{ scale: 0.95 }}
                            >
                                💝 Support Our Work
                            </motion.a>
                            <motion.a
                                href="/contact"
                                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 font-bold py-4 px-8 rounded-full text-lg"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                🤝 Get Involved
                            </motion.a>
                        </motion.div>

                        <motion.div className="flex flex-col md:flex-row justify-center items-center gap-8 text-lg" variants={fadeInUp}>
                            <div className="flex items-center gap-3">
                                <FaPhone className="text-blue-300" />
                                <span>+917985947303</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <FaEnvelope className="text-blue-300" />
                                <span>support@ummeedkikiran.org</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <FaMapMarkerAlt className="text-blue-300" />
                                <span>Varanasi, Uttar Pradesh</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>
        </div>
    );
};

export default AchievementsPage; 